import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { AsyncBoundary, Icon } from '@kollez/design-system';
import {
  useDrives, useOffers, useApplications, useNexieDrafts, useTpoDashboard,
} from '@/lib/queries';
import { formatLPA, formatIndianDate } from '@kollez/shared';
import { isOfferAwaitingSignoff } from '@kollez/shared/enums';

function timeOfDayGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

/**
 * /tpo/today (F84c) — direct port of kollez/pages/tpo/dashboard.html.
 *   Greeting header + 4 KPI cards + Needs-you queue + Nexie nudge + drives-in-progress table + timeline + branch bars + next big date
 */
export function TpoTodayPage() {
  const drives = useDrives();
  const offers = useOffers();
  const apps = useApplications();
  const drafts = useNexieDrafts('draft');
  const dashboard = useTpoDashboard();
  const dashKpis = dashboard.data?.kpis;
  const nav = useNavigate();

  const driveRows = drives.data?.drives ?? [];
  const offerRows = offers.data?.offers ?? [];
  const appRows = apps.data?.applications ?? [];
  const draftRows = drafts.data?.drafts ?? [];

  const kpis = useMemo(() => {
    const placed = offerRows.filter((o) => /accept/i.test(o.status)).length;
    const totalOffers = offerRows.length;
    const median = medianCtc(offerRows);
    // "Not applying" surrogate — students eligible but with 0 applications; not derivable client-side, so leave 0
    return { placed, totalOffers, median };
  }, [offerRows]);

  const queue = useMemo(() => {
    return [
      // Offers awaiting sign-off
      ...offerRows
        .filter((o) => isOfferAwaitingSignoff(o.status) || (o.checks && !(o as { allChecksPassed?: boolean }).allChecksPassed))
        .slice(0, 4)
        .map((o) => ({
          _id: o._id,
          urgent: true,
          icon: 'mail-check',
          title: `Sign off ${(o as { partner?: { name?: string } }).partner?.name ?? 'offer'} · ${(o as { candidateName?: string }).candidateName ?? '—'}`,
          due: 'Blocks release',
          href: `/tpo/offers`,
        })),
      // Nexie drafts to review
      ...draftRows.slice(0, 3).map((d) => ({
        _id: d._id,
        urgent: false,
        icon: 'sparkles',
        title: `Review Nexie draft · ${d.kind.replace(/_/g, ' ')}`,
        due: 'Never sent without you',
        href: `/nexie`,
      })),
    ].slice(0, 6);
  }, [offerRows, draftRows]);

  // Step 86 — DRIVE_STATUS is lowercase: {draft, open, assessing, interviewing, offers,
  // closed, cancelled}. Old filter checked 'Closed' / 'Archived' — neither existed in the
  // enum, so every drive fell through as "active" (including long-closed rows). TPO Today
  // was showing closed drives in the drives-in-progress table.
  const active = driveRows.filter((d) => d.status !== 'closed' && d.status !== 'cancelled');
  const nextDay1 = active.find((d) => (d as { tier?: number }).tier === 1);

  return (
    <div className="stack">
      <style>{`
        .queue { margin: 0; padding: 0; list-style: none; }
        .queue li { display: grid; grid-template-columns: 28px minmax(0,1fr) auto; gap: 12px; align-items: center; padding: 14px 20px; border-bottom: 1px solid rgb(var(--line-tertiary)); }
        .queue li:last-child { border-bottom: 0; }
        .queue .q-ico { width: 28px; height: 28px; border-radius: 6px; display: grid; place-items: center; border: 1px solid rgb(var(--line-secondary)); color: rgb(var(--ink-secondary)); }
        .queue li[data-urgent] .q-ico { border-color: rgb(var(--margin)); color: rgb(var(--ink-danger)); }
        .queue .q-due { font-size: 0.75rem; color: rgb(var(--ink-tertiary)); }
        .queue li[data-urgent] .q-due { color: rgb(var(--ink-danger)); font-weight: 550; }
        .funnel { display: flex; height: 8px; border-radius: 4px; overflow: hidden; background: rgb(var(--bg-tertiary)); min-width: 120px; }
        .funnel span { display: block; height: 100%; }
        .funnel span:nth-child(1) { background: rgb(var(--ink-accent) / .25); }
        .funnel span:nth-child(2) { background: rgb(var(--ink-accent) / .55); }
        .funnel span:nth-child(3) { background: rgb(var(--ink-accent)); }
        .funnel span:nth-child(4) { background: rgb(var(--ink-success)); }
        .big-serif { font-family: "Newsreader", Georgia, serif; font-weight: 500; font-size: 3rem; line-height: 1; letter-spacing: -0.02em; }
      `}</style>

      {/* Deep-audit fixes (Page 30/N):
          - Eyebrow hardcoded 'Batch 2026'. A different tenant or a
            different year would keep seeing 2026. Dropped the batch
            token entirely; the date is enough context.
          - Greeting was always 'Good morning' regardless of when the
            TPO opened the page. Now uses timeOfDayGreeting().
          - 'Post a drive' button used <Icon name='plus' /> — 'plus' is
            NOT in the registry (verified via grep). Every render
            produced a broken slot. Replaced with the registered
            'file-plus' icon which reads correctly for the action.
      */}
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">{new Date().toLocaleString('en-IN', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' })}</p>
          <h1 className="h-page">{timeOfDayGreeting()}</h1>
          <p className="subhead">
            {queue.length > 0 ? `${queue.length} things need you today.` : 'Nothing waiting on you today.'}
          </p>
        </div>
        <div className="ph-actions">
          <Link className="btn btn-outline" to="/nexie"><Icon name="sparkles" size={14} />Ask Nexie</Link>
          <Link className="btn btn-primary" to="/tpo/drives/new"><Icon name="file-plus" size={14} />Post a drive</Link>
        </div>
      </div>

      {/* KPIs */}
      {dashKpis && (
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
          <div className="card kpi">
            <span className="kpi-label">Placement rate</span>
            <span className="kpi-value">{dashKpis.placementRatePct}%</span>
            <span className="kpi-meta">
              {dashKpis.acceptedOffersLifetime} of {dashKpis.totalStudents} students
            </span>
          </div>
          <Link className="card kpi" to="/tpo/offers">
            <span className="kpi-label">Pending sign-off</span>
            <span className="kpi-value">{dashKpis.pendingSignoffCount}</span>
            <span className="kpi-meta">offers awaiting release</span>
          </Link>
          <div className="card kpi">
            <span className="kpi-label">Applications · 7d</span>
            <span className="kpi-value">{dashKpis.applicationsLast7d}</span>
            <span className="kpi-meta">across all drives</span>
          </div>
          <Link className="card kpi" to="/tpo/calendar">
            <span className="kpi-label">Interviews today</span>
            <span className="kpi-value">{dashKpis.interviewsTodayCount}</span>
            <span className="kpi-meta">{dashKpis.drivesTodayCount} drives running</span>
          </Link>
        </div>
      )}

      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <Link className="card kpi" to="/tpo/students">
          <span className="kpi-label">Batch placed</span>
          <span className="kpi-value">{kpis.placed}</span>
          <span className="kpi-meta">accepted offers this season</span>
        </Link>
        <Link className="card kpi" to="/tpo/offers">
          <span className="kpi-label">Offers this season</span>
          <span className="kpi-value">{kpis.totalOffers}</span>
          <span className="kpi-meta">across all drives</span>
        </Link>
        <div className="card kpi">
          <span className="kpi-label">Median CTC</span>
          <span className="kpi-value">{kpis.median ? formatLPA(kpis.median) : '—'}</span>
          <span className="kpi-meta">from accepted offers</span>
        </div>
        <Link className="card kpi" to="/tpo/students">
          <span className="kpi-label">Applications</span>
          <span className="kpi-value">{appRows.length}</span>
          <span className="kpi-meta">this season</span>
        </Link>
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_340px] items-start">
        <div className="grid gap-5 min-w-0">
          {/* Needs you */}
          <section className="card">
            <div className="card-head">
              <div className="flex items-baseline gap-2">
                <h2 className="h-section">Needs you</h2>
                <span className="helper">{queue.length} open</span>
              </div>
            </div>
            {queue.length === 0 ? (
              <div className="p-8 text-center grid gap-2 justify-items-center">
                <Icon name="check-check" size={22} className="text-ink-tertiary" />
                <div className="cell-title">Nothing waiting on you</div>
                <p className="helper max-w-sm">Offers to sign off, drive requests and roster problems land here first.</p>
              </div>
            ) : (
              <ul className="queue">
                {queue.map((q) => (
                  <li key={q._id} data-urgent={q.urgent || undefined}>
                    <span className="q-ico">
                      {q.icon === 'sparkles' ? <Icon name="sparkles" size={14} /> : <Icon name="check-check" size={14} />}
                    </span>
                    <div className="min-w-0">
                      <div className="cell-title truncate">{q.title}</div>
                      <div className="q-due">{q.due}</div>
                    </div>
                    <Link className="btn btn-ghost btn-sm" to={q.href}>Open</Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Nexie nudge */}
          {draftRows.length > 0 && (
            <div className="ai-suggest">
              <Icon name="sparkles" size={14} />
              <div className="flex-1">
                <strong>Nexie has {draftRows.length} draft{draftRows.length === 1 ? '' : 's'} for your review.</strong>{' '}
                Nothing is sent until you review it.
              </div>
              <Link className="btn btn-outline btn-sm shrink-0" to="/nexie">Review drafts</Link>
            </div>
          )}

          {/* Drives in progress */}
          <section className="card">
            <div className="card-head">
              <h2 className="h-section">Drives in progress</h2>
              <Link className="link text-sm" to="/tpo/drives">All drives</Link>
            </div>
            <AsyncBoundary
              kind="table"
              inCard
              loading={drives.isLoading}
              error={drives.error}
              empty={!drives.isLoading && active.length === 0}
              emptyTitle="No drives running"
              emptyHint="Post a drive and eligible students see it on their Explore page immediately."
              onRetry={() => void drives.refetch()}
            >
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Drive</th>
                      <th>Stage</th>
                      <th className="r">Applied</th>
                      <th className="r hide-mobile">Offers</th>
                    </tr>
                  </thead>
                  <tbody>
                    {active.slice(0, 6).map((d) => (
                      <tr key={d._id} data-href={`/tpo/drives/${d._id}`} onClick={() => nav(`/tpo/drives/${d._id}`)} style={{ cursor: 'pointer' }}>
                        <td>
                          <div className="cell-title truncate">{d.driveName}</div>
                          <div className="cell-sub">{d.college?.name ?? '—'}</div>
                        </td>
                        <td><span className="badge">{d.status.replace(/_/g, ' ')}</span></td>
                        <td className="r">{(d as { applicationsCount?: number }).applicationsCount ?? '—'}</td>
                        <td className="r hide-mobile">{(d as { offersCount?: number }).offersCount ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </AsyncBoundary>
          </section>
        </div>

        <div className="grid gap-5 min-w-0">
          {/* Next big date */}
          {nextDay1 && (
            <section className="card card-pad">
              <p className="eyebrow">Next Day 1 drive</p>
              <div className="flex items-end gap-3 mt-1">
                <span className="big-serif">
                  {Math.max(0, Math.round((new Date(nextDay1.driveDate).getTime() - Date.now()) / 86400000))}
                </span>
                <span className="pb-1 text-sm text-ink-secondary">
                  days to<br />
                  <b className="text-ink-primary">{nextDay1.driveName}</b>
                </span>
              </div>
              <p className="helper mt-3">{formatIndianDate(nextDay1.driveDate)} · {nextDay1.college?.name ?? '—'}</p>
              <Link className="btn btn-outline btn-sm mt-2" to={`/tpo/drives/${nextDay1._id}`}>Open drive</Link>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

function medianCtc(offers: Array<{ status: string; ctc?: { amountInr: number } }>): number {
  const accepted = offers
    .filter((o) => /accept/i.test(o.status) && typeof o.ctc?.amountInr === 'number')
    .map((o) => o.ctc!.amountInr);
  if (accepted.length === 0) return 0;
  accepted.sort((a, b) => a - b);
  const mid = Math.floor(accepted.length / 2);
  return accepted.length % 2 === 0 ? Math.round((accepted[mid - 1]! + accepted[mid]!) / 2) : accepted[mid]!;
}
